import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// ============================================================================
// Server-DTOer for 3D lagerlayout (speiler WarehouseLayoutDTOs.cs)
// ============================================================================

export interface ServerWarehouseLayout {
  warehouseID: string;
  layoutName?: string | null;
  lengthMeters: number;
  widthMeters: number;
  heightMeters: number;
  gridUnitMeters: number;
  hasBackgroundImage: boolean;
  backgroundImageType?: string | null;
  backgroundScale?: number | null;
  backgroundOffsetX?: number | null;
  backgroundOffsetY?: number | null;
  lastUpdate?: string | null;
  updatedBy?: string | null;
}

export interface ServerWarehouseLevel {
  levelNo: number;
  levelName: string;
  elevationMeters: number;
  floorPolygon?: string | null;
}

export interface ServerRackPosition {
  rackNumber: string;
  levelNo: number;
  positionX: number;
  positionY: number;
  rotation: number;
  rackLabel?: string | null;
  color?: string | null;
  widthOverride?: number | null;
  depthOverride?: number | null;
  heightOverride?: number | null;
}

export interface ServerBinPosition {
  binCode: string;
  levelNo: number;
  placementKind: 'FLOOR' | 'WALL' | 'HANGING' | 'CABINET' | 'DOCK';
  positionX: number;
  positionY: number;
  rotation: number;
  widthOverride?: number | null;
  depthOverride?: number | null;
  heightOverride?: number | null;
  color?: string | null;
}

export interface ServerLayoutObject {
  id: number;
  levelNo: number;
  objectType: string;
  objectName?: string | null;
  positionX: number;
  positionY: number;
  width: number;
  depth: number;
  height?: number | null;
  rotation: number;
  color?: string | null;
  stcZoneID?: string | null;
}

export interface FullServerLayout {
  layout: ServerWarehouseLayout | null;
  levels: ServerWarehouseLevel[];
  rackPositions: ServerRackPosition[];
  binPositions: ServerBinPosition[];
  objects: ServerLayoutObject[];
}

export interface ServerRackVerticalGeometry {
  code: string;
  widthMeters: number;
}

export interface ServerRackShelfGeometry {
  code: string;
  heightMeters: number;
}

export interface ServerRackGeometry {
  rackNumber: string;
  widthMeters: number;
  depthMeters: number;
  heightMeters: number;
  verticals: ServerRackVerticalGeometry[];
  shelves: ServerRackShelfGeometry[];
  locationCount: number;
  hasBinSizeData: boolean;
}

export interface ServerSpecialBin {
  binCode: string;
  description1?: string | null;
  stcZoneID?: string | null;
  binSizeNo: number;
  widthMeters?: number | null;
  depthMeters?: number | null;
  heightMeters?: number | null;
}

export interface ServerWarehouseGeometry {
  racks: ServerRackGeometry[];
  specialBins: ServerSpecialBin[];
}

export interface ServerUnplacedItems {
  rackNumbers: string[];
  specialBins: ServerSpecialBin[];
}

/** Innsikt-verdi per bin (speiler LayoutInsightBinDto) — value=null betyr ukjent (nøytral grå) */
export interface ServerInsightBin {
  binCode: string;
  /** null for spesiallokasjoner utenfor reol-grid */
  rackNumber?: string | null;
  vertical?: string | null;
  shelf?: string | null;
  /** 0–1 normalisert intensitet, null = ukjent */
  value?: number | null;
  /** Råverdi bak normaliseringen (antall transaksjoner, %, dager osv.) */
  raw?: number | null;
}

/** Innsikt-overlay for hele lageret (speiler WarehouseInsightsDto) */
export interface ServerWarehouseInsights {
  mode: string;
  days: number;
  generatedAt: string;
  /** Største råverdi — brukes til legendens skala */
  maxRaw: number;
  bins: ServerInsightBin[];
}

/** Én saldorad i innholdsdialogen (speiler LocationContentRowDto) */
export interface ServerLocationContentRow {
  binCode: string;
  vertical?: string | null;
  shelf?: string | null;
  itemNo: string;
  itemDescription?: string | null;
  quantity: number;
  batchNo?: string | null;
  serialNo?: string | null;
  expiryDate?: string | null;
  lastMovementDate?: string | null;
}

/** Innhold for en reol eller én enkeltlokasjon (speiler RackContentsDto) */
export interface ServerRackContents {
  rackNumber: string;
  binCount: number;
  binsWithStock: number;
  rows: ServerLocationContentRow[];
}

/** Lokasjonstreff for varesøk (speiler ItemLocationHitDto) */
export interface ServerItemLocationHit {
  binCode: string;
  /** null når lokasjonen er en spesiallokasjon utenfor reol-grid */
  rackNumber?: string | null;
  quantity: number;
}

/** Varesøk-resultat (speiler ItemSearchResultDto) */
export interface ServerItemSearchResult {
  itemNo: string;
  description?: string | null;
  totalQuantity: number;
  locations: ServerItemLocationHit[];
}

/** Én kandidat-mållokasjon for flytt (speiler MoveTargetBinDto) */
export interface ServerMoveTargetBin {
  binCode: string;
  /** null for spesiallokasjoner utenfor reol-grid */
  rackNumber?: string | null;
  vertical?: string | null;
  shelf?: string | null;
  isEmpty: boolean;
  currentQuantity: number;
  stcZoneID?: string | null;
  binTypeNo: number;
  binSizeNo: number;
}

/** Filtrerte kandidat-mål for flytt (speiler MoveTargetsDto) */
export interface ServerMoveTargets {
  totalCandidates: number;
  /** true = resultatet er kuttet til maks (stram inn filtrene) */
  truncated: boolean;
  bins: ServerMoveTargetBin[];
}

/** Åpen flytteordre (StcIntRelOrder < 60) med reol-mapping (speiler PendingMoveDto) */
export interface ServerPendingMove {
  stcOrderNo: number;
  itemNo: string;
  itemDescription?: string | null;
  quantity: number;
  fromBinCode: string;
  toBinCode: string;
  /** null når lokasjonen er en spesiallokasjon utenfor reol-grid */
  fromRackNumber?: string | null;
  toRackNumber?: string | null;
  orderStatus: number;
  lineStatus: number;
  created?: string | null;
  createdBy?: string | null;
}

/** Soneoversikt-rad — ledig kapasitet som TALL per sone (speiler ZoneSummaryDto) */
export interface ServerZoneSummary {
  stcZoneID: string;
  description?: string | null;
  totalBins: number;
  emptyBins: number;
  binsWithStock: number;
}

/** Filterparametre for move-targets-endepunktet */
export interface MoveTargetsQuery {
  emptyOnly?: boolean;
  pickableOnly?: boolean;
  zoneId?: string;
  binTypeNo?: number;
  binSizeNo?: number;
  excludeBinCode?: string;
}

export interface UpsertLayoutRequest {
  layoutName?: string | null;
  lengthMeters: number;
  widthMeters: number;
  heightMeters: number;
  gridUnitMeters: number;
  expectedLastUpdate?: string | null;
}

// ============================================================================
// Service
// ============================================================================

class WarehouseLayoutService {
  private async ensureOk(response: Response, action: string): Promise<Response> {
    if (!response.ok) {
      let message = `${action}: ${response.status} ${response.statusText}`;
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch {
        // ignorer — bruk standardmelding
      }
      const error = new Error(message) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
    return response;
  }

  async getFullLayout(warehouseId: string): Promise<FullServerLayout> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}`
    );
    await this.ensureOk(response, 'Kunne ikke hente lagerlayout');
    return response.json();
  }

  async upsertLayout(warehouseId: string, request: UpsertLayoutRequest): Promise<ServerWarehouseLayout> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}`,
      request
    );
    await this.ensureOk(response, 'Kunne ikke lagre lagerlayout');
    return response.json();
  }

  async upsertRackPositions(
    warehouseId: string,
    positions: ServerRackPosition[],
    replaceAll: boolean
  ): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/rack-positions`,
      { positions, replaceAll }
    );
    await this.ensureOk(response, 'Kunne ikke lagre reolposisjoner');
  }

  async upsertBinPositions(
    warehouseId: string,
    positions: ServerBinPosition[],
    replaceAll: boolean
  ): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/bin-positions`,
      { positions, replaceAll }
    );
    await this.ensureOk(response, 'Kunne ikke lagre lokasjonsposisjoner');
  }

  async replaceObjects(
    warehouseId: string,
    objects: Omit<ServerLayoutObject, 'id'>[]
  ): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/objects`,
      objects
    );
    await this.ensureOk(response, 'Kunne ikke lagre layout-objekter');
  }

  async upsertLevels(warehouseId: string, levels: ServerWarehouseLevel[]): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/levels`,
      { levels }
    );
    await this.ensureOk(response, 'Kunne ikke lagre nivåer');
  }

  async getGeometry(warehouseId: string): Promise<ServerWarehouseGeometry> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/geometry`
    );
    await this.ensureOk(response, 'Kunne ikke hente reolgeometri');
    return response.json();
  }

  async getInsights(
    warehouseId: string,
    mode: string,
    days: number
  ): Promise<ServerWarehouseInsights> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/insights?mode=${encodeURIComponent(mode)}&days=${days}`
    );
    await this.ensureOk(response, 'Kunne ikke hente innsiktsdata');
    return response.json();
  }

  /** Innhold («hva ligger her?») for en reol — eller én enkeltlokasjon (send BinCode som rackNumber) */
  async getRackContents(warehouseId: string, rackNumber: string): Promise<ServerRackContents> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/rack-contents/${encodeURIComponent(rackNumber)}`
    );
    await this.ensureOk(response, 'Kunne ikke hente innhold');
    return response.json();
  }

  /** Varesøk («hvor ligger vare X?») — matcher varenr/beskrivelse, maks 15 varer */
  async findItem(warehouseId: string, query: string): Promise<ServerItemSearchResult[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/find-item?query=${encodeURIComponent(query)}`
    );
    await this.ensureOk(response, 'Varesøket feilet');
    return response.json();
  }

  /** Åpne flytteordrer (StcIntRelOrder < 60) med reol-mapping — piler i tvillingen (maks 200) */
  async getPendingMoves(warehouseId: string): Promise<ServerPendingMove[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/pending-moves`
    );
    await this.ensureOk(response, 'Kunne ikke hente ventende flytt');
    return response.json();
  }

  /** Soneoversikt («hvor har jeg plass?») — sortert med flest ledige lokasjoner først */
  async getZoneSummary(warehouseId: string): Promise<ServerZoneSummary[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/zone-summary`
    );
    await this.ensureOk(response, 'Kunne ikke hente soneoversikt');
    return response.json();
  }

  /** Filtrerte kandidat-mål for flytt (steg 6) — maks 500, truncated=true betyr «stram inn filtrene» */
  async getMoveTargets(warehouseId: string, query: MoveTargetsQuery): Promise<ServerMoveTargets> {
    const params = new URLSearchParams();
    if (query.emptyOnly) params.append('emptyOnly', 'true');
    if (query.pickableOnly) params.append('pickableOnly', 'true');
    if (query.zoneId) params.append('zoneId', query.zoneId);
    if (query.binTypeNo !== undefined) params.append('binTypeNo', String(query.binTypeNo));
    if (query.binSizeNo !== undefined) params.append('binSizeNo', String(query.binSizeNo));
    if (query.excludeBinCode) params.append('excludeBinCode', query.excludeBinCode);

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/move-targets?${params.toString()}`
    );
    await this.ensureOk(response, 'Kunne ikke hente kandidat-lokasjoner');
    return response.json();
  }

  async getUnplaced(warehouseId: string): Promise<ServerUnplacedItems> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/unplaced`
    );
    await this.ensureOk(response, 'Kunne ikke hente uplasserte elementer');
    return response.json();
  }

  async updateBackground(
    warehouseId: string,
    request: {
      imageBase64?: string | null;
      imageType?: string | null;
      scale?: number | null;
      offsetX?: number | null;
      offsetY?: number | null;
    }
  ): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/background`,
      request
    );
    await this.ensureOk(response, 'Kunne ikke lagre plantegning');
  }

  /** Henter plantegningen som object-URL (kalleren må revoke ved opprydding). */
  async getBackgroundObjectUrl(warehouseId: string): Promise<string | null> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/background`
    );
    if (response.status === 404) return null;
    await this.ensureOk(response, 'Kunne ikke hente plantegning');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }
}

export const warehouseLayoutService = new WarehouseLayoutService();
